import { type AuthenticatedUser, type Lang } from '@owl-nest/api-client/latest'
import { lens } from '@owl-nest/config'

export type Notification = {
  tags: 'info' | 'success' | 'error'
  children: string
}

type UFE = {
  notifications?: Notification[]
  data: {
    project: {
      id: number
      slug: string
    }
    news: {
      id: number
      slug: string
    }
  }
  flags: {
    liveEnabled: boolean
    liveVisitorsCountDisplay: boolean
    paypalEnabled: boolean
  }
  gtmKey: string
  request: {
    path: string
  }
  urls: {
    live_count: string
  }
  user: AuthenticatedUser & {
    is_authenticated: boolean
    nb_projects: number
    project_list_url?: string
    settings_url?: string
    transactions_url: string
  }
}

type ProjectData = {
  images: {
    adviceMainImage: string
    checklist: {
      '1x': string
      '2x': string
      '3x': string
    }
    comingSoonBackground: string
    cpf: string
    following: {
      x1: string
      x2: string
      x3: string
    }
    goal: {
      [id: string]: string
    }
    hint: {
      [id: string]: string
    }
    sdgLogo: {
      [lang in Lang]?: string
    }
    sparkles: string
  }
  videos: {
    creatorWelcoming: {
      placeholder: {
        [lang in Lang]: {
          '1x': string
          '2x': string
          '3x': string
        }
      }
      version: {
        rewardBased: { [lang in Lang]: string }
        donationBased: { [lang in Lang]: string }
      }
    }
    onboardingWelcome: {
      placeholder: {
        [lang in Lang]: {
          x1: string
          x2: string
          x3: string
        }
      }
      version: {
        [lang in Lang]: string
      }
    }
  }
}

type Window = {
  PROJECT_DATA: ProjectData
  UFE: UFE
  STATIC_URL: string
}

const windowLens = lens<Window>()

export const STATIC_URL = windowLens.get('STATIC_URL').value(window)

export const NOTIFICATIONS = windowLens.get('UFE').get('notifications').value(window, { trust: true, forgive: true })

export const CURRENT_USER = windowLens.get('UFE').get('user').value(window, { trust: true })

export const FLAGS = windowLens.get('UFE').get('flags').value(window)

export const GTM_KEY = windowLens.get('UFE').get('gtmKey').value(window)

export const IMAGES = windowLens.get('PROJECT_DATA').get('images').value(window, { trust: true })

export const NEWS = windowLens.get('UFE').get('data').get('news').value(window, { forgive: true, silent: true })

export const PROJECT = windowLens.get('UFE').get('data').get('project').value(window, { trust: true })

export const REQUEST = windowLens.get('UFE').get('request').value(window)

export const VIDEOS = windowLens.get('PROJECT_DATA').get('videos').value(window, { trust: true })

export const LIVE_PROJECT_URL = windowLens.get('UFE').get('urls').get('live_count').value(window, { forgive: true })

// LINKS

export const LINKS = {
  deliveryHelper: {
    en: 'https://support.ulule.com/hc/en-us/articles/360015608958',
    es: 'https://support.ulule.com/hc/es-es/articles/360015608958',
    fr: 'https://support.ulule.com/hc/fr-fr/articles/360015608958',
    it: 'https://support.ulule.com/hc/it/articles/360015608958',
    nl: 'https://support.ulule.com/hc/nl/articles/360015608958',
  },
  newsHelper: {
    en: 'https://community.ulule.com/topics/publish-news-on-your-page-why-and-how-17709/#main-32422',
    es: 'https://community.ulule.com/topics/las-noticias-de-tu-proyecto-por-que-y-como-publicar-noticias-16881/#main-30713',
    fr: 'https://community.ulule.com/topics/-news-mon-projet-pourquoi-comment-et-quand-3300/',
    it: 'https://community.ulule.com/topics/publish-news-on-your-page-why-and-how-17709/#main-32422',
    nl: 'https://community.ulule.com/topics/nieuwsberichten-op-je-pagina-waarom-en-hoe-17713/#main-32436',
  },
  risksHelper: {
    en: 'https://support.ulule.com/hc/en-us/articles/360014571178',
    es: 'https://support.ulule.com/hc/en-us/articles/360014571178',
    fr: 'https://support.ulule.com/hc/fr-fr/articles/360014369337-Risques-et-d%C3%A9fis-d-un-projet-Ulule',
    it: 'https://support.ulule.com/hc/en-us/articles/360014571178',
    nl: 'https://support.ulule.com/hc/en-us/articles/360014571178',
  },
  sdg: {
    en: 'https://www.un.org/sustainabledevelopment/',
    es: 'https://www.un.org/sustainabledevelopment/es/',
    fr: 'https://www.un.org/sustainabledevelopment/fr/objectifs-de-developpement-durable/',
    it: 'https://www.un.org/sustainabledevelopment/',
    nl: 'https://www.un.org/sustainabledevelopment/',
  },
  rewardsHelper: {
    en: 'https://community.ulule.com/topics/rewards-ulule-how-invent-and-create-them-906/',
    es: 'https://community.ulule.com/topics/recompensas-en-ulule-como-inventarlas-y-crearlas-1-8787/#main-11079',
    fr: 'https://community.ulule.com/topics/rewards-ulule-how-invent-and-create-them-906/',
    it: 'https://community.ulule.com/topics/il-reward-based-crowdfunding-e-le-ricompense-quale-strategia-per-crearle-9865/#main-18479',
    nl: 'https://community.ulule.com/topics/beloningen-wat-zijn-de-belangrijkste-niveaus-13538/',
  },
}
